import { Component, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type Props = { children: ReactNode; fallback?: ReactNode }
type State = { error: Error | null }

// Without this, an uncaught render error in any page unmounts the whole
// React root — the tab goes fully blank with no way back (this is how a
// broken third-party component, e.g. the Mux video player, was reported as
// "a blank page" rather than a contained failure). Catches at the app root
// so one page's bug can't take down navigation to every other page.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("[ErrorBoundary]", error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      // A caller can pass `fallback` to contain a non-essential widget — an
      // optional lazy chunk that fails to load should hide, not take over the
      // screen with the full-page error.
      if (this.props.fallback !== undefined) {
        return this.props.fallback
      }
      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="max-w-md space-y-4 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-destructive" aria-hidden="true" />
            <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              This page hit an unexpected error. Try again, and let us know if it keeps happening.
            </p>
            <a href="/" className={cn(buttonVariants({ size: "sm" }))}>
              Back to safety
            </a>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
